import { compile } from "path-to-regexp";
import has from "lodash-es/has";
import join from "lodash-es/join";
import isEmpty from "lodash-es/isEmpty";

const cache = {};

export class Router {
  paths = {};

  get(name, args) {
    const path = this.paths[name];

    if (!path) {
      throw `Path ${name} not found`;
    }

    if (isEmpty(args)) {
      return path;
    }

    if (!has(cache, path)) {
      cache[path] = compile(path);
    }
    const stringifiedObj = Object.fromEntries(
      Object.entries(args).map(([key, value]) => [key, String(value)]),
    );
    return cache[path](stringifiedObj);
  }

  add(name, path = name, routerCallback) {
    if (this.paths[name]) {
      throw new Error(`Route ${name} already defined`);
    }

    if (path[0] !== "/") {
      path = "/" + path;
    }

    this.paths[name] = path;

    if (routerCallback) {
      const subRouter = {
        add: (subName, subPath = subName, routerCallback) => {
          if (subPath[0] !== "/") {
            subPath = "/" + subPath;
          }

          this.add(name + "." + subName, path + subPath, routerCallback);
          return subRouter;
        },
      };

      routerCallback(subRouter);
    }

    return this;
  }
}

const paths = new Router();

paths
  .add("/")
  .add("dashboard", "/dashboard")
  .add("accounts", "/accounts")
  .add("account", "/accounts/:id", (router) => {
    router.add("dashboard");
    router.add("license");
    router.add("registered");
    router.add("events");
    router.add("files");
    router.add("push_centers");
    router.add("packages", "/packages", (router) => {
      router.add("plink");
      router.add("pmax");
      router.add("ota");
      router.add("usc");
    });
  })
  .add("licenses", "/licenses", (router) => {
    router.add("templates", "/");
    router.add("template", "template/:license_template_id", (router) => {
      router.add("limits");
      router.add("features");
      router.add("accounts");
    });
    router.add("limits");
    router.add("features");
  })
  .add("essentials", "/essentials", (router) => {
    router.add("versions", "/");
    router.add("xmls", "/xmls", (router) => {
      router.add("neo");
      router.add("pmax");
    });
    router.add("version", "version/:version", (router) => {
      router.add("files", "/files");
      router.add("languages", "/languages");
    });
  })
  .add("packages", "/packages", (router) => {
    router.add("plink");
    router.add("pmax");
    router.add("ota", "ota", (router) => {
      router.add("hmac");
    });
    router.add("usc");
    router.add("package", "/:package_id/accounts");
  })
  .add("push_centers", "/push_centers", (router) => {
    router.add("apns");
    router.add("fcm");
    router.add("apns_center", "apns/:id_center/accounts");
    router.add("fcm_center", "fcm/:id_center/accounts");
  })
  .add("iu_versions", "/incremental_upgrade")
  .add("iu_version", "/incremental_upgrade/:iu_version", (router) => {
    router.add("packages");
    router.add("dnf");
  })
  .add("action_log", "/action_log")
  .add("system", "/system", (router) => {
    router.add("roles", "roles");
    router.add("role", "roles/:role_id");
    router.add("users");
    router.add("tls");
    router.add("events");
    router.add("services");
    router.add("settings");
  });

export default function path(name, args) {
  return paths.get(name, args);
}

export function pathParent(name, args) {
  return path(name, args) + "/*";
}

export function extendPath(name, extend, args) {
  return path(join([name, ...(extend ? [extend] : [])], "."), args);
}

export function pathLastRouterChild({ name, nodes, ...args }) {
  return pathLastChild(name, nodes, args);
}

export function pathLastChild(name, nodes = 1, args) {
  const fullPath = path(name, args);
  const sections = name.split(".");
  const parentSection = sections.splice(0, sections.length - nodes).join(".");
  const parentPath = path(parentSection, args);
  return fullPath.replace(parentPath, "");
}
